import { useQuery } from '@tanstack/react-query';
import type {
  MeetingPageDebrief,
  MeetingPageResponse,
  MeetingPrepIntake,
} from '@wundo/shared/meetingPage.ts';
import { formatDayAndMonth } from '@/components/agenda/meetingFormat.ts';
import { meetingPageTitle } from '@/components/meetings/meetingPageTitle.ts';
import type { MeetingPanel } from '@/components/meetings/meetingPanel.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import {
  type ChatPageNudge,
  type ChatPageOffer,
  type ChatQuickActionOffer,
  usePublishChatPageOffer,
} from '@/lib/chatPageOffer.ts';
import { useChatPageNudge } from '@/lib/useChatPageNudge.ts';
import { fetchMeetingPage } from '@/lib/meetingPage.ts';
import { m } from '@/paraglide/messages.js';

/* `page` is undefined while it is still loading. */
interface MeetingChatOfferSource {
  meetingId: string;
  page: MeetingPageResponse | undefined;
  nudge: ChatPageNudge | undefined;
}

/**
 * What the chat offers beside a meeting. Preparing a conversation and writing
 * it up are two jobs, so each tab gets its own openers and quick actions; the
 * lead action is the one the tab is for.
 */
function meetingChatOffer(
  panel: MeetingPanel,
  source: MeetingChatOfferSource,
): ChatPageOffer {
  const { meetingId, page, nudge } = source;
  return {
    /*
     * Ranks behind both openers, so a meeting Wundo still has questions about never shows
     * one (DEV-72).
     */
    ...(nudge ? { nudge } : {}),
    line: chatLine(panel, page ? meetingPageTitle(page) : null),
    ...(panel === 'prep' ? prepOffer(meetingId, page) : reportOffer(meetingId, page)),
  };
}

/* The intake and the recap are about the conversation to come: preparation only. */
function prepOffer(
  meetingId: string,
  page: MeetingPageResponse | undefined,
): Pick<ChatPageOffer, 'quickActions' | 'intake' | 'recap'> {
  const intake = page?.prep_intake ?? null;
  const recap = page?.recap ?? null;
  return {
    quickActions: prepQuickActions(intake, recap !== null),
    ...(intake
      ? {
          intake: {
            opener: intake.opener,
            meetingId,
            /*
             * A page reloaded halfway through hands the drawer that conversation, not the
             * opener again.
             */
            conversationId: intake.conversation_id,
          },
        }
      : {}),
    /*
     * The date is written in the reader's own locale, the same "28 mei" the agenda
     * writes.
     */
    ...(recap
      ? {
          recap: {
            text: recap.text,
            meetingId,
            title: m.chat_recap_card_title({
              date: formatDayAndMonth(new Date(recap.meeting_starts_at)),
            }),
            linkText: m.chat_recap_card_link({
              date: formatDayAndMonth(new Date(recap.meeting_starts_at)),
            }),
            linkMeetingId: recap.meeting_id,
            conversationId: recap.conversation_id,
          },
        }
      : {}),
  };
}

/* The debrief asks how a sitting went, so only the verslag offers it. */
function reportOffer(
  meetingId: string,
  page: MeetingPageResponse | undefined,
): Pick<ChatPageOffer, 'quickActions' | 'debrief'> {
  const debrief = page?.debrief ?? null;
  /* A verslag from a pasted note is the user's own words: feedback needs a transcript. */
  const hasTranscript =
    page?.materials.some((material) => material.kind === 'transcript') ?? false;
  return {
    quickActions: reportQuickActions(debrief, hasTranscript),
    ...(debrief
      ? {
          debrief: {
            opener: debrief.opener,
            meetingId,
            conversationId: debrief.conversation_id,
          },
        }
      : {}),
  };
}

/* Until the meeting has a name there is nothing to name it after. */
function chatLine(panel: MeetingPanel, meeting: string | null): string {
  if (meeting === null) return m.chat_line_meeting();
  if (panel === 'prep') return m.chat_line_meeting_prep({ meeting });
  return m.chat_line_meeting_report({ meeting });
}

function prepQuickActions(
  intake: MeetingPrepIntake | null,
  hasRecap: boolean,
): ChatQuickActionOffer[] {
  return [
    /*
     * On a meeting Wundo cannot prepare on its own this is what the tab is for,
     * so it leads. It sends nothing: it stands the drawer where this meeting's
     * opener belongs and hands the caret over to answer in their own words.
     */
    ...(intake
      ? [
          {
            text: m.chat_quick_prep_intake(),
            icon: 'prepare' as const,
            behavior: 'intake' as const,
          },
        ]
      : []),
    { text: m.chat_quick_prep_tips(), icon: 'tips' },
    { text: m.chat_quick_prep_roleplay(), icon: 'roleplay' },
    ...(hasRecap ? [{ text: m.chat_quick_prep_recap(), icon: 'recap' as const }] : []),
  ];
}

/*
 * On a meeting Wundo has nothing to write the verslag from, the debrief is the
 * only thing offered, the way the preparation's own opener is. Feedback needs a
 * transcript to be feedback about, so without one nothing is worth offering.
 */
function reportQuickActions(
  debrief: MeetingPageDebrief | null,
  hasTranscript: boolean,
): ChatQuickActionOffer[] {
  if (debrief) {
    return [{ text: m.chat_quick_report_debrief(), icon: 'recap', behavior: 'intake' }];
  }
  if (hasTranscript) return [{ text: m.chat_quick_report_check(), icon: 'check' }];
  return [];
}

/**
 * Publish what this meeting offers the drawer, for as long as the page is up.
 * The nudge comes from a faster request than the page's openers and is ranked
 * last, so it joins only once the page has settled: otherwise it would show
 * first and then be replaced by an opener or recap.
 */
export function useMeetingChatOffer(meetingId: string, panel: MeetingPanel): void {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  /*
   * The chat's line needs the meeting's name, and the view is already reading
   * it under this key — so this is the cache, not a second request.
   */
  const { data: page, isPending: pageUnsettled } = useQuery({
    queryKey: ['meetingPage', user?.id, meetingId],
    queryFn: () => fetchMeetingPage(user!.id, meetingId),
    enabled: Boolean(user?.id),
  });

  /*
   * Which nudge, if any, belongs beside this tab — the two meeting-scoped ones
   * are about the conversation on screen, so the tab is part of the question.
   */
  const nudge = useChatPageNudge(
    panel === 'prep'
      ? { page: 'meeting-prep', meeting_id: meetingId }
      : { page: 'meeting-report', meeting_id: meetingId },
  );

  usePublishChatPageOffer(
    meetingChatOffer(panel, {
      meetingId,
      page,
      nudge: pageUnsettled ? undefined : nudge,
    }),
  );
}
